/**
 * Browser file-download helpers shared by every "export → download a Blob" flow
 * (analytics reports, account data export, …). DOM-only: `downloadBlob` guards
 * the environment so a server/native import surfaces a clear error rather than
 * silently no-op'ing. Filename parsing is pure and runs anywhere.
 */

import { ApiErrorClass } from '../types/api/errors';

/**
 * Parse the download filename out of a `Content-Disposition` header, falling
 * back to `fallback` when the header is missing or unparseable.
 */
export function filenameFromContentDisposition(
  header: string | null | undefined,
  fallback: string
): string {
  if (!header) return fallback;
  const match = header.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const raw = match?.[1];
  if (!raw) return fallback;
  const cleaned = raw.replace(/['"]/g, '').trim();
  return cleaned || fallback;
}

/**
 * Trigger a browser download of `blob` as `filename` via a transient object URL.
 * Throws an `ApiErrorClass` outside a DOM environment (e.g. SSR / React Native).
 */
export function downloadBlob(blob: Blob, filename: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new ApiErrorClass(
      'File download is not available in this environment',
      'SERVER_ERROR',
      500
    );
  }

  let url: string | null = null;
  try {
    url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch {
    throw new ApiErrorClass('Failed to download file', 'SERVER_ERROR', 500);
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
}
